// components/layout/Sidebar.js
// Hover-expand sidebar — collapsed (64px) by default, expands to 256px on hover
// No toggle buttons — maximises main content space

import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { businessAPI } from '../../services/businessAPI';
import { useSidebar } from '../../contexts/SidebarContext';

export default function Sidebar() {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { user, logout } = useAuth();
  const { setIsExpanded } = useSidebar();
  const [isHovered, setIsHovered] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState('diy');
  const [logoError, setLogoError] = useState(false);
  const leaveTimer = useRef(null);

  const isCollapsed = !isHovered;

  // Load subscription tier
  useEffect(() => {
    businessAPI.getSubscription()
      .then(r => { if (r.success) setSubscriptionTier(r.data.currentTier); })
      .catch(() => setSubscriptionTier('diy'));
  }, []);

  const handleMouseEnter = () => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    setIsHovered(true);
    setIsExpanded(true);
  };

  const handleMouseLeave = () => {
    leaveTimer.current = setTimeout(() => {
      setIsHovered(false);
      setIsExpanded(false);
    }, 120);
  };

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  const tierHierarchy = { diy: 0, assisted: 1, managed: 2 };
  const hasAccess = (t) => !t || (tierHierarchy[subscriptionTier] ?? 0) >= (tierHierarchy[t] ?? 0);

  const userInfo = {
    name:     user?.businessName || user?.email?.split('@')[0] || 'User',
    email:    user?.email || '',
    initials: (user?.businessName || user?.email?.split('@')[0] || 'U')
      .split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2),
  };

  const navItems = [
    {
      name: 'Dashboard', path: '/dashboard',
      icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
    },
    {
      name: 'Marketing', path: '/marketing',
      icon: 'M13 10V3L4 14h7v7l9-11h-7z',
    },
    {
      name: 'Results', path: '/results',
      icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
    },
    {
      name: 'Settings', path: '/settings',
      icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
    },
  ];

  const isActive = (path) => location.pathname === path;

  const SvgIcon = ({ path }) => (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  );

  return (
    <>
      {/* Mobile overlay */}
      {!isCollapsed && (
        <div className="fixed inset-0 bg-black/20 z-40 lg:hidden" onClick={() => setIsHovered(false)} />
      )}

      <aside
        className={`
          fixed left-0 top-0 h-full z-50 flex flex-col
          bg-slate-900 border-r border-slate-700
          transition-all duration-200 ease-in-out overflow-hidden
          ${isCollapsed ? 'w-16' : 'w-64 shadow-2xl'}
        `}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Logo / Brand */}
        <div className="flex-shrink-0 h-16 flex items-center border-b border-slate-700 overflow-hidden">
          {isCollapsed ? (
            <div className="w-16 flex items-center justify-center">
              {!logoError ? (
                <img src="/dt_logo.png" alt="DT" className="w-8 h-8 object-contain"
                  onError={() => setLogoError(true)} />
              ) : (
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-xs font-bold text-white">{userInfo.initials}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="px-5 whitespace-nowrap">
              <div className="text-lg font-bold text-white tracking-tight">Digital Trent</div>
              <div className="text-[10px] text-slate-400 truncate max-w-[180px]">{userInfo.email}</div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-hidden">
          {navItems.map((item) => {
            const active = isActive(item.path);
            return (
              <div key={item.path} className="relative group">
                <Link
                  to={item.path}
                  className={`
                    relative flex items-center rounded-xl px-3 py-2.5
                    text-sm font-medium transition-all duration-150 overflow-hidden
                    ${active ? 'text-blue-400 bg-blue-500/10' : 'text-slate-400 hover:text-white hover:bg-slate-800'}
                    ${isCollapsed ? 'justify-center' : 'justify-start gap-3'}
                  `}
                >
                  {active && !isCollapsed && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-blue-400 rounded-r-full" />
                  )}
                  <SvgIcon path={item.icon} />
                  {!isCollapsed && (
                    <div className="flex flex-1 items-center justify-between whitespace-nowrap">
                      <span>{item.name}</span>
                      {item.badge && (
                        <span className="px-2 py-0.5 bg-blue-600 text-white text-[10px] font-semibold rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </div>
                  )}
                  {active && !isCollapsed && (
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                  )}
                </Link>

                {/* Tooltip when collapsed */}
                {isCollapsed && (
                  <div className="
                    absolute left-full ml-3 top-1/2 -translate-y-1/2
                    px-3 py-1.5 bg-slate-800 border border-slate-700 text-white text-xs font-medium
                    rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible
                    transition-all duration-150 pointer-events-none whitespace-nowrap z-50
                  ">
                    {item.name}
                    {item.badge && (
                      <span className="ml-2 px-1.5 py-0.5 bg-blue-600 text-white text-[10px] rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Bottom — Sign Out */}
        <div className="flex-shrink-0 border-t border-slate-700 p-2 overflow-hidden">
          <button
            onClick={handleLogout}
            className={`
              w-full flex items-center rounded-xl px-3 py-2.5
              text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10
              transition-all duration-150 overflow-hidden
              ${isCollapsed ? 'justify-center' : 'justify-start gap-3'}
            `}
          >
            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            {!isCollapsed && <span className="whitespace-nowrap">Sign Out</span>}
          </button>
        </div>
      </aside>
    </>
  );
}
